import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface StaffOrganization {
  id: string
  name: string
  domain?: string
  inboundEmailAddress?: string
  industry?: string
  companySize?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  staff: Array<{
    id: string
    firstName: string
    lastName: string
    email: string
    isActive: boolean
  }>
  clientOrganizations: Array<{
    id: string
    name: string
    isActive: boolean
  }>
}

const StaffOrganizations: React.FC = () => {
  const { showSuccess, showError } = useToast()
  const [organizations, setOrganizations] = useState<StaffOrganization[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingOrg, setEditingOrg] = useState<StaffOrganization | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    inboundEmailAddress: '',
    industry: '',
    companySize: ''
  })

  useEffect(() => {
    fetchOrganizations()
  }, [])

  const fetchOrganizations = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/staff-organizations')
      if (response.data.success) {
        setOrganizations(response.data.data)
      }
    } catch (error: any) {
      showError('Failed to fetch organizations')
      console.error('Error fetching organizations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const response = await axios.post('/api/staff-organizations', formData)
      if (response.data.success) {
        showSuccess('Staff organization created successfully')
        setShowCreateForm(false)
        setFormData({
          name: '',
          domain: '',
          inboundEmailAddress: '',
          industry: '',
          companySize: ''
        })
        fetchOrganizations()
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to create organization')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingOrg) return

    try {
      const response = await axios.put(`/api/staff-organizations/${editingOrg.id}`, {
        ...formData,
        isActive: editingOrg.isActive
      })
      if (response.data.success) {
        showSuccess('Staff organization updated successfully')
        setEditingOrg(null)
        setFormData({
          name: '',
          domain: '',
          inboundEmailAddress: '',
          industry: '',
          companySize: ''
        })
        fetchOrganizations()
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to update organization')
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this organization?')) return

    try {
      const response = await axios.delete(`/api/staff-organizations/${id}`)
      if (response.data.success) {
        showSuccess('Staff organization deleted successfully')
        fetchOrganizations()
      }
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to delete organization')
    }
  }

  const handleToggleActive = async (org: StaffOrganization) => {
    try {
      const response = await axios.put(`/api/staff-organizations/${org.id}`, {
        ...org,
        isActive: !org.isActive
      })
      if (response.data.success) {
        showSuccess(`Organization ${!org.isActive ? 'activated' : 'deactivated'} successfully`)
        fetchOrganizations()
      }
    } catch (error: any) {
      showError('Failed to update organization status')
    }
  }

  const openEditForm = (org: StaffOrganization) => {
    setEditingOrg(org)
    setFormData({
      name: org.name,
      domain: org.domain || '',
      inboundEmailAddress: org.inboundEmailAddress || '',
      industry: org.industry || '',
      companySize: org.companySize || ''
    })
  }

  const closeForms = () => {
    setShowCreateForm(false)
    setEditingOrg(null)
    setFormData({
      name: '',
      domain: '',
      inboundEmailAddress: '',
      industry: '',
      companySize: ''
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Staff Organizations</h1>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Create Organization
          </button>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingOrg) && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-medium mb-4">
              {editingOrg ? 'Edit Organization' : 'Create New Organization'}
            </h2>
            <form onSubmit={editingOrg ? handleUpdate : handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Domain</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Inbound Email Address</label>
                  <input
                    type="email"
                    value={formData.inboundEmailAddress}
                    onChange={(e) => setFormData({ ...formData, inboundEmailAddress: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="support@yourdomain.com"
                  />
                </div>
                                  <div>
                    <label className="block text-sm font-medium text-gray-700">Industry</label>
                    <input
                      type="text"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Company Size</label>
                  <select
                    value={formData.companySize}
                    onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Select company size</option>
                    <option value="1-2">1-2 employees</option>
                    <option value="3-10">3-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="501-1000">501-1000 employees</option>
                    <option value="1000+">1000+ employees</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeForms}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingOrg ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Organizations List */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {organizations.map((org) => (
              <li key={org.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900">{org.name}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        org.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {org.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-6 mt-2 text-sm text-gray-500">
                      {org.domain && (
                        <span>🌐 {org.domain}</span>
                      )}
                      {org.inboundEmailAddress && (
                        <span>📧 {org.inboundEmailAddress}</span>
                      )}
                      {org.industry && (
                        <span>🏭 {org.industry}</span>
                      )}
                      {org.companySize && (
                        <span>👥 {org.companySize}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <span>👥 {org.staff.length} staff</span>
                      <span>🏢 {org.clientOrganizations.length} client organizations</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditForm(org)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleActive(org)}
                      className={`text-sm font-medium ${
                        org.isActive 
                          ? 'text-red-600 hover:text-red-800' 
                          : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      {org.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(org.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {organizations.length === 0 && (
            <div className="px-6 py-12 text-center text-gray-500">
              No staff organizations found. Create your first one above.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default StaffOrganizations
